import React, { useState } from "react";

// Import all page components
import Navigation from "./components/navbar";
import HomePage from "./components/home";
import AboutPage from "./components/about";
import ServicesPage from "./components/services";
import GalleryPage from "./components/gallery";
import ContactPage from "./components/contact";
import OnlineServicesPage from "./components/onlineservices";
import DonationScreen from "./components/donation";
import AdminPaymentApproval from "./admin/payments";

const App = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return <HomePage setCurrentPage={setCurrentPage} />;
      case "about":
        return <AboutPage setCurrentPage={setCurrentPage} />;
      case "services":
        return <ServicesPage setCurrentPage={setCurrentPage} />;
      case "gallery":
        return <GalleryPage setCurrentPage={setCurrentPage} />;
      case "donation":
        return <DonationScreen setCurrentPage={setCurrentPage} />;
      case "contact":
        return <ContactPage setCurrentPage={setCurrentPage} />;
      case "online-services":
        return <OnlineServicesPage setCurrentPage={setCurrentPage} />;
      case "payments":
        return <AdminPaymentApproval setCurrentPage={setCurrentPage} />;
      default:
        return <HomePage setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />

      <main>{renderPage()}</main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* About Section */}
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full overflow-hidden mr-3 flex-shrink-0 border-2 border-yellow-300 bg-yellow-100 shadow-sm">
                  <img
                    src="/images/Logo01.png"
                    alt="Temple logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-bold leading-tight pt-1">
                  Sri Kalikamundeshwari Temple
                </h3>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                A sacred place of worship, community, and spiritual growth.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b border-gray-700 pb-2">
                Quick Links
              </h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => setCurrentPage("home")}
                    className="text-gray-300 hover:text-yellow-400 transition-colors text-sm flex items-center group"
                  >
                    <span className="mr-2 group-hover:mr-3 transition-all">
                      ›
                    </span>
                    Home
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage("about")}
                    className="text-gray-300 hover:text-yellow-400 transition-colors text-sm flex items-center group"
                  >
                    <span className="mr-2 group-hover:mr-3 transition-all">
                      ›
                    </span>
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage("services")}
                    className="text-gray-300 hover:text-yellow-400 transition-colors text-sm flex items-center group"
                  >
                    <span className="mr-2 group-hover:mr-3 transition-all">
                      ›
                    </span>
                    Services
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setCurrentPage("gallery")}
                    className="text-gray-300 hover:text-yellow-400 transition-colors text-sm flex items-center group"
                  >
                    <span className="mr-2 group-hover:mr-3 transition-all">
                      ›
                    </span>
                    Gallery
                  </button>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b border-gray-700 pb-2">
                Our Services
              </h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="mr-2 text-yellow-400">✓</span>
                  <span>Daily Worship</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-yellow-400">✓</span>
                  <span>Special Pujas</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-yellow-400">✓</span>
                  <span>Wedding Ceremonies</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-yellow-400">✓</span>
                  <span>Spiritual Counseling</span>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold border-b border-gray-700 pb-2">
                Contact Us
              </h3>
              <div className="space-y-3 text-gray-300 text-sm">
                <div className="flex items-start">
                  <span className="mr-3 text-yellow-400 flex-shrink-0 mt-1">
                    📍
                  </span>
                  <p className="leading-relaxed">
                    No. 264-1, Nanjapuram, TVS Main Road, Kothogandapalli Post,
                    Krishnagiri dist, Hosur - 635109
                  </p>
                </div>
                <div className="flex items-center">
                  <span className="mr-3 text-yellow-400">📞</span>
                  <a
                    href="tel:+919845411941"
                    className="hover:text-yellow-400 transition-colors"
                  >
                    +91 98454 11941
                  </a>
                </div>
                <div className="flex items-start">
                  <span className="mr-3 text-yellow-400 flex-shrink-0">✉️</span>
                  <a
                    href="mailto:srikalikamundeshwaritemple@gmail.com"
                    className="hover:text-yellow-400 transition-colors break-all"
                  >
                    srikalikamundeshwaritemple@gmail.com
                  </a>
                </div>
                <div className="flex items-center">
                  <span className="mr-3 text-yellow-400">🕐</span>
                  <span>6:00 AM - 9:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 mt-10 pt-6">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <p className="text-gray-400 text-sm text-center sm:text-left">
                &copy; 2025 ONEVEGA Systems (India) Pvt Ltd. All rights
                reserved.
              </p>
              <div className="flex space-x-6">
                <button
                  onClick={() => setCurrentPage("home")}
                  className="text-gray-400 hover:text-yellow-400 transition-colors text-sm"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => setCurrentPage("home")}
                  className="text-gray-400 hover:text-yellow-400 transition-colors text-sm"
                >
                  Terms of Service
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideLeft {
          from {
            transform: translateX(-50px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideRight {
          from {
            transform: translateX(50px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.8s ease-out;
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }

        .animate-slideLeft {
          animation: slideLeft 0.8s ease-out;
        }

        .animate-slideRight {
          animation: slideRight 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default App;
